import { useRef } from "react";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import { AnimatePresence, motion } from "framer-motion";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { LAYOUTS } from "../../layouts.config";
import PageMiniPreview from "../PageMiniPreview.jsx";



const MiniHalf = PageMiniPreview;


// ─── SpreadMiniGrid — миниатюра разворота ─────────────────────────────

function SpreadMiniGridThumb({ pageData, height = 46, pagesCount, pageWidthMm, pageHeightMm }) {
  const leftLayout  = LAYOUTS.find(l => l.id === pageData?.leftLayoutId)  ?? LAYOUTS[0];
  const rightLayout = LAYOUTS.find(l => l.id === pageData?.rightLayoutId) ?? LAYOUTS[0];

  return (
    <Stack direction="row" sx={{ height }}>
      <MiniHalf layout={leftLayout}  photos={pageData?.left}  empty={false}
        side="left"  pagesCount={pagesCount} pageWidthMm={pageWidthMm} pageHeightMm={pageHeightMm} />
      <Box
        sx={{
          width: "2.5px", flexShrink: 0,
          background: "linear-gradient(90deg, rgba(0,0,0,0.09) 0%, rgba(255,255,255,0.38) 50%, rgba(0,0,0,0.07) 100%)",
        }}
      />
      <MiniHalf layout={rightLayout} photos={pageData?.right} empty={false}
        side="right" pagesCount={pagesCount} pageWidthMm={pageWidthMm} pageHeightMm={pageHeightMm} />
    </Stack>
  );
}

// ─── Одна миниатюра в боковой панели ─────────────────────────────────────────

function SideThumb({ renderCoverThumbnail, pageData, index, active, activeSide, onClickSide, onClick, onDelete, thumbH = 46, thumbW = 80, coverBgColor, coverStickerImage, coverTitleColor, coverAccentColor, coverText, coverYear, coverBackPhotos, frontLayoutTag, backLayoutTag, pagesCount, pageWidthMm, pageHeightMm }) {
  const backIsMinimal = backLayoutTag === "minimal";
  const frontIsPhoto  = frontLayoutTag === "photo";
  const frontPhotoUrl = coverBackPhotos?.[4]?.url ?? null;
  const isEndpaper = index === 1;
  const isCover    = index === 0;

  let label;
  if (isCover)    label = "Обложка";
  // else if (isEndpaper) label = "Форзац";
  else            label = String((index - 1) * 2 + 1);

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        px: 0.75, py: 0.5,
        width: isCover ? thumbW * 2 + 3 : thumbW * 2 + 3,
        display: "flex", flexDirection: "column", alignItems: "center", gap: "3px",
        position: "relative",
        flexShrink: 0,
        "&:hover .side-del": { opacity: 1 },
      }}
    >
      {/* Превью разворота */}
      <Box
        sx={{
          width: "100%",
          borderRadius: "4px",
          overflow: "hidden",
          border: active
            ? "2px solid var(--joy-palette-primary-400)"
            : "2px solid transparent",
          boxShadow: active
            ? "0 0 0 1px color-mix(in srgb, var(--joy-palette-primary-400) 28%, transparent)"
            : "0 1px 5px rgba(0,0,0,0.13)",
          transition: "border-color 0.15s, box-shadow 0.15s",
          bgcolor: "background.surface",
        }}
      >
        {isCover && renderCoverThumbnail ? renderCoverThumbnail() : isCover ? (
          /* ── Миниатюра обложки: задняя + передняя со слотами фото ── */
          <Stack direction="row" sx={{ height: thumbH }}>
            {/* Задняя часть */}
            <Box sx={{
              flex: 1, position: "relative", overflow: "hidden",
              bgcolor: coverBgColor ?? "#22223b",
              display: "flex", flexDirection: "column",
              alignItems: "center",
              p: "3px",
              pb: "10px",
            }}>
              {/* Заголовок + год + сетка — только если задний макет НЕ "Только лого" */}
              {!backIsMinimal && (
                <>
                  {/* Заголовок — ~12.6% от высоты, прижат к низу блока */}
                  <Box sx={{
                    height: "12.6%", width: "88.6%",
                    display: "flex", flexDirection: "column",
                    justifyContent: "flex-end", alignItems: "center",
                  }}>
                    {coverText && (
                      <Box sx={{
                        fontSize: "3.5px", fontWeight: 800, color: coverTitleColor ?? "#fff",
                        textAlign: "center", lineHeight: 1.1,
                        textTransform: "uppercase", maxWidth: "100%",
                        overflow: "hidden", textOverflow: "ellipsis",
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                      }}>
                        {coverText}
                      </Box>
                    )}
                  </Box>
                  {/* Год — ~2.6% */}
                  {coverYear && (
                    <Box sx={{
                      fontSize: "2.5px", fontWeight: 800,
                      color: coverAccentColor ?? "rgba(255,255,255,0.7)",
                      textAlign: "center", lineHeight: 1, mt: "1px", mb: "1px",
                    }}>
                      {coverYear}
                    </Box>
                  )}
                  {/* Грид фото — ~58% высоты, ~66% ширины */}
                  <Box sx={{
                    width: "66%", flex: 1,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr 1fr",
                    gap: "1px",
                  }}>
                  {[0, 1, 2, 3].map(idx => (
                    <Box key={idx} sx={{
                      borderRadius: "1px", overflow: "hidden",
                      bgcolor: coverBackPhotos?.[idx]?.url
                        ? "transparent"
                        : "rgba(255,255,255,0.15)",
                    }}>
                      {coverBackPhotos?.[idx]?.url && (
                        <Box component="img" src={coverBackPhotos[idx].url}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      )}
                    </Box>
                  ))}
                  </Box>
                </>
              )}
              {/* Логотип */}
              <Box sx={{
                position: "absolute", bottom: 2, left: 0, right: 0,
                display: "flex", justifyContent: "center",
              }}>
                <Box component="img" src="/logo.png" draggable={false} sx={{
                  height: 6, width: "auto", maxWidth: "70%",
                  display: "block", opacity: 0.85,
                  pointerEvents: "none", userSelect: "none",
                }} />
              </Box>
            </Box>

            {/* Корешок */}
            <Box sx={{
              width: "3px", flexShrink: 0,
              background: "linear-gradient(90deg, rgba(0,0,0,0.15) 0%, rgba(255,255,255,0.3) 50%, rgba(0,0,0,0.12) 100%)",
            }} />

            {/* Передняя часть */}
            <Box sx={{
              flex: 1, position: "relative", overflow: "hidden",
              bgcolor: coverBgColor ?? "#22223b",
              display: "flex", flexDirection: "column",
              alignItems: "center",
              p: "3px",
              pb: "10px",
            }}>
              {/* Заголовок — прижат к низу блока */}
              <Box sx={{
                height: "12.6%", width: "88.6%",
                display: "flex", flexDirection: "column",
                justifyContent: "flex-end", alignItems: "center",
              }}>
                {coverText && (
                  <Box sx={{
                    fontSize: "3.5px", fontWeight: 800, color: coverTitleColor ?? "#fff",
                    textAlign: "center", lineHeight: 1.1,
                    textTransform: "uppercase", maxWidth: "100%",
                    overflow: "hidden", textOverflow: "ellipsis",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>
                    {coverText}
                  </Box>
                )}
              </Box>
              {/* Год */}
              {coverYear && (
                <Box sx={{
                  fontSize: "2.5px", fontWeight: 800,
                  color: coverAccentColor ?? "rgba(255,255,255,0.7)",
                  textAlign: "center", lineHeight: 1, mt: "1px", mb: "1px",
                }}>
                  {coverYear}
                </Box>
              )}
              {/* Передняя зона: фото (макет "Фото") или стикер */}
              {frontIsPhoto ? (
                <Box sx={{
                  flex: 1, width: "68%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", borderRadius: "1px",
                  bgcolor: frontPhotoUrl ? "transparent" : "rgba(255,255,255,0.15)",
                  border: frontPhotoUrl ? "none" : "0.75px dashed rgba(255,255,255,0.3)",
                }}>
                  {frontPhotoUrl && (
                    <Box component="img" src={frontPhotoUrl}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  )}
                </Box>
              ) : coverStickerImage ? (
                <Box sx={{ flex: 1, width: "70%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <Box component="img" src={coverStickerImage}
                    sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
                </Box>
              ) : (
                <Box sx={{ flex: 1, width: "65%", borderRadius: "1px", border: "0.75px dashed rgba(255,255,255,0.3)" }} />
              )}
              {/* Логотип */}
              <Box sx={{
                position: "absolute", bottom: 2, left: 0, right: 0,
                display: "flex", justifyContent: "center",
              }}>
                <Box component="img" src="/logo.png" draggable={false} sx={{
                  height: 6, width: "auto", maxWidth: "70%",
                  display: "block", opacity: 0.85,
                  pointerEvents: "none", userSelect: "none",
                }} />
              </Box>
            </Box>
          </Stack>
        ) : isEndpaper ? (
          <Stack direction="row" sx={{ height: thumbH }}>
            {/* Левая — всегда пустая */}
            <Box sx={{ flex: 1, bgcolor: "#f5f5f8" }} />
            <Box sx={{ width: "2px", flexShrink: 0, background: "linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0.35) 50%, rgba(0,0,0,0.05) 100%)" }} />
            {/* Правая — через MiniHalf с реальным layout */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", overflow: "hidden" }}>
              <MiniHalf
                layout={LAYOUTS.find(l => l.id === pageData?.rightLayoutId) ?? LAYOUTS.find(l => l.id === "single")}
                photos={pageData?.right}
                empty={false}
                side="right" pagesCount={pagesCount} pageWidthMm={pageWidthMm} pageHeightMm={pageHeightMm}
              />
            </Box>
          </Stack>
        ) : (
          (
            <Stack direction="row" sx={{ height: thumbH, cursor: "pointer" }}>
              {/* Левая половина */}
              <Box
                onClick={(e) => { e.stopPropagation(); onClickSide("left"); }}
                sx={{
                  flex: 1, minWidth: 0, overflow: "hidden", display: "flex",
                  borderRadius: "2px 0 0 2px",
                  outline: (active && activeSide === "left") ? "2px solid var(--joy-palette-primary-400)" : "none",
                  outlineOffset: "-2px",
                  transition: "outline 0.15s",
                }}
              >
                <MiniHalf
                  layout={LAYOUTS.find(l => l.id === pageData?.leftLayoutId) ?? LAYOUTS[0]}
                  photos={pageData?.left}
                  empty={false}
                  side="left" pagesCount={pagesCount} pageWidthMm={pageWidthMm} pageHeightMm={pageHeightMm}
                />
              </Box>
              <Box sx={{
                width: "2.5px", flexShrink: 0,
                background: "linear-gradient(90deg, rgba(0,0,0,0.09) 0%, rgba(255,255,255,0.38) 50%, rgba(0,0,0,0.07) 100%)",
              }} />
              {/* Правая половина */}
              <Box
                onClick={(e) => { e.stopPropagation(); onClickSide("right"); }}
                sx={{
                  flex: 1, minWidth: 0, overflow: "hidden", display: "flex",
                  borderRadius: "0 2px 2px 0",
                  outline: (active && activeSide === "right") ? "2px solid var(--joy-palette-primary-400)" : "none",
                  outlineOffset: "-2px",
                  transition: "outline 0.15s",
                }}
              >
                <MiniHalf
                  layout={LAYOUTS.find(l => l.id === pageData?.rightLayoutId) ?? LAYOUTS[0]}
                  photos={pageData?.right}
                  empty={false}
                  side="right" pagesCount={pagesCount} pageWidthMm={pageWidthMm} pageHeightMm={pageHeightMm}
                />
              </Box>
            </Stack>
          )
        )}
      </Box>

      {/* Подпись */}
      <Typography
        level="body-xs"
        sx={{
          fontSize: "9.5px",
          lineHeight: 1,
          color: active ? "primary.500" : "text.tertiary",
          fontWeight: active ? 700 : 400,
          transition: "color 0.15s",
        }}
      >
        {label}
      </Typography>

      {/* Кнопка удалить (только для разворотов, не для обложки/форзаца) */}
      {/* {onDelete && !isEndpaper && !isCover && (
        <IconButton
          className="side-del"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm("Удалить разворот?")) onDelete(index);
          }}
          sx={{
            position: "absolute", top: -3, right: 0,
            minWidth: 18, minHeight: 18, p: 0,
            opacity: 0, transition: "opacity 0.15s",
            bgcolor: "rgba(180,0,0,0.72)",
            borderRadius: 10,
            "&:hover": { bgcolor: "rgba(210,0,0,0.9)", opacity: "1 !important" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 10, color: "white" }} />
        </IconButton>
      )} */}
    </Box>
  );
}

// ─── Боковая панель ───────────────────────────────────────────────────────────

export default function BottomPagePanelMini({ renderCoverThumbnail, pages, activePage, setActivePage, activeSide, setActiveSide, centerTab, setCenterTab, addPage, deletePage, isMobileOpen, onCloseMobile, bookSize, coverBgColor, coverStickerImage, coverTitleColor, coverAccentColor, coverText, coverYear, coverBackPhotos, frontLayoutTag, backLayoutTag }) {
  const scrollbarSx = {
    // "&::-webkit-scrollbar": { width: "3px" },
    // // "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
    // "&::-webkit-scrollbar-thumb": {
    //   bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 22%, transparent)",
    //   borderRadius: "4px", margin: "1px"
    // },
  };

  const THUMB_BASE = 80; // базовая ширина одной страницы в px
  const thumbW = bookSize ? THUMB_BASE : 80;
  const thumbH = bookSize
    ? Math.round((bookSize.heightMm / bookSize.widthMm) * THUMB_BASE)
    : 46;

  const panelContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRight: "1px solid",
        borderColor: "divider",
        bgcolor: "background.level1",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Заголовок (виден только на мобиле в виде кнопки закрыть) */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "flex-end",
          px: 1, py: 0.75,
          borderBottom: "1px solid", borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <IconButton size="sm" variant="plain" color="neutral" onClick={onCloseMobile}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Список миниатюр */}
      <Box sx={{ flex: 1, px: 2, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", alignItems: "center", py: 1, ...scrollbarSx }}>
        {pages.map((pg, i) => (
          <SideThumb
            key={i}
            pageData={pg}
            index={i}
            active={activePage === i}
            activeSide={activeSide}
            pagesCount={Math.max(0, (pages.length - 2) * 2)}
            pageWidthMm={bookSize?.widthMm}
            pageHeightMm={bookSize?.heightMm}
            onClickSide={(side) => {
              setActivePage(i);
              setActiveSide(side);
              // Если не на странице редактора — переходим туда
              if (centerTab !== "pages") setCenterTab("pages");
              if (onCloseMobile) onCloseMobile();
            }}
            onClick={() => {
              setActivePage(i);
              if (centerTab !== "pages") setCenterTab("pages");
              if (onCloseMobile) onCloseMobile();
            }}
            onDelete={i > 1 ? deletePage : null}
            thumbH={thumbH}
            thumbW={thumbW}
            isCover={i === 0}
            coverBgColor={i === 0 ? coverBgColor : undefined}
            coverStickerImage={i === 0 ? coverStickerImage : undefined}
            coverTitleColor={i === 0 ? coverTitleColor : undefined}
            coverAccentColor={i === 0 ? coverAccentColor : undefined}
            coverText={i === 0 ? coverText : undefined}
            coverYear={i === 0 ? coverYear : undefined}
            renderCoverThumbnail={i === 0 ? renderCoverThumbnail : undefined}
            coverBackPhotos={i === 0 ? coverBackPhotos : undefined}
            frontLayoutTag={i === 0 ? frontLayoutTag : undefined}
            backLayoutTag={i === 0 ? backLayoutTag : undefined}
          />
        ))}

        {/* Кнопка + добавить разворот */}
        {/* <Box
          onClick={addPage}
          sx={{
            mx: 0.75, my: 0.5,
            borderRadius: "4px",
            height: thumbH,        // ← динамическая высота как у миниатюр
            minWidth: 60,
            flexShrink: 0,         // ← не сжимается
            border: "1.5px dashed color-mix(in srgb, var(--joy-palette-primary-400) 32%, transparent)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.15s",
            "&:hover": {
              borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 62%, transparent)",
              bgcolor: "color-mix(in srgb, var(--joy-palette-primary-500) 5%, transparent)",
            },
          }}
        >
          <Typography sx={{ fontSize: 18, color: "primary.300", lineHeight: 1 }}>+</Typography>
        </Box> */}
      </Box>
    </Box>
  );

  return (
    <>
      {/* ── Desktop: всегда видна ── */}
      <Box sx={{ display: { xs: "none", md: "flex" }, width: "100%", height: "100%" }}>
        {panelContent}
      </Box>

      {/* ── Mobile: slide-in оверлей ── */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Блюр-оверлей */}
            <motion.div
              key="left-panel-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1199,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(20,20,40,0.38)",
              }}
              onClick={onCloseMobile}
            />

            {/* Сама панель — вылетает слева */}
            <motion.div
              key="left-panel-drawer"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                height: "100dvh",
                zIndex: 1200,
              }}
            >
              {panelContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}